/**
 * Ryan O'Dowd
 * 2022-06-03
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useMemo,
  useState,
} from 'react';
import {
  TouchableOpacity,
  View,
} from 'react-native';
import CPassage from '@classes/Passage';
import PassageDisplay from '@components/PassageDisplay';
import Text from '@components/common/Text';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';

const WrinkleChip = (props: WrinkleChipProps) => {
  const styles = hooks.useStyles(getStyles);

  const settingsPreferredVerseSelectionMethod = hooks.useTypedSelector((state) => state.settingsPreferredVerseSelectionMethod);

  const levelNumbersBadge = useMemo(() => {
    if (!props.showLevelNumbers) {
      return null;
    }

    let minLevelNumber = props.wrinkles[0].level_number;
    let maxLevelNumber = props.wrinkles[0].level_number;
    props.wrinkles.forEach((w) => {
      if (w.level_number < minLevelNumber) {
        minLevelNumber = w.level_number;
      }
      if (w.level_number > maxLevelNumber) {
        maxLevelNumber = w.level_number;
      }
    });

    return (
      <View style={styles.levelNumberBadge}>
        <Text style={styles.levelNumberBadgeText}>{minLevelNumber === maxLevelNumber ? `${minLevelNumber}` : `${minLevelNumber}-${maxLevelNumber}`}</Text>
      </View>
    );
  }, [props.wrinkles, props.showLevelNumbers, styles.levelNumberBadge, styles.levelNumberBadgeText]);

  const [bookApiId, chapterNumber, verseNumber] = props.wrinkles[0].verse_api_id.split('.');
  const book = hooks.useTypedSelector((state) => state.books.find((b) => b.api_id === bookApiId));

  const [_showVerses, setShowVerses] = useState(false);

  const passage = new CPassage(-1, props.wrinkles[0].translation_id, book.id, chapterNumber, verseNumber, +props.wrinkles[props.wrinkles.length - 1].verse_api_id.split('.')[2]);

  let borderColor;
  if (props.wrinkles.some((w) => new Date(w.date_next_srs_level) <= new Date())) {
    if (props.wrinkles.some((w) => w.level_number < globals.srsLevelWrinkleIsEarnedAt)) {
      borderColor = styles.colors.warnIncorrect;
    } else {
      borderColor = styles.colors.accent;
    }
  }

  let borderStyle = 'solid';
  let pausedStyles = null;
  const someWrinklesArePaused = props.wrinkles.some((w) => w.is_paused);
  const allWrinklesArePaused = props.wrinkles.every((w) => w.is_paused);
  if (someWrinklesArePaused) {
    if (!allWrinklesArePaused) {
      borderStyle = 'dashed';
    } else {
      if (!_showVerses) {
        pausedStyles = styles.pausedWrinkleContainer;
      }
      borderColor = styles.colors.lightGray;
    }
  }

  return (
    <View
      style={[
        styles.container,
        _showVerses && styles.containerExpanded,
        borderColor && {borderColor},
        borderStyle && {borderStyle},
        pausedStyles,
      ]}
    >
      <TouchableOpacity
        style={[styles.titleContainer, _showVerses && styles.titleContainerExpanded]}
        onPress={() => {
          setShowVerses((curr) => !curr);
          // @TODO: if necessary, auto scroll so that this all fits on screen
        }}
      >
        {levelNumbersBadge}
        <Text style={[styles.chipText, _showVerses && styles.chipTextExpanded]}>{_showVerses ? `${passage.prettyPassageName()} (${passage.getTranslation().abbreviation})` : passage.abbreviatedPassageName()}</Text>{/* @TODO: translation should be smaller text */}
      </TouchableOpacity>
      {_showVerses &&
        <PassageDisplay// @TODO: should this have a max height and be scrollable instead of having a (potentially) huge height (if it's a long passage)?
          passages={[passage]}
          hideLicensing={true}
          showFormatting={false}
          showFootnotes={false}
          showHeadings={false}
          referenceOnLineTwo={true}
          alwaysShowSelectionMenu={true}
          hideCompare={true}
          hideAddToGoal={true}
          hideNumbersTextToggle={props.verseNumbersOnly}
          hideShare={true}
          isPressDisabled={false}
          isUnattachedToGoal={true}
          verseNumbersOnly={props.verseNumbersOnly || settingsPreferredVerseSelectionMethod === 'numbers'}// @TODO: this should be the responsibility of PassageDisplay to determine, not passed in (unless it's an override)
          showWrinkleBadges={true}
          verseTextStyles={styles.verseText}
          passageForContext={passage}
          lessonAndTestAvailableWithoutSelection={true}
        />
      }
    </View>
  );
};

class WrinkleChipProps {
  wrinkles: any[];

  verseNumbersOnly?: boolean;
  showLevelNumbers?: boolean;
};

export default WrinkleChip;
